// src/components/Dashboard/ClaimRecordList.jsx
//
// Lista de RECLAMOS (una tarjeta por reclamo, con su paso actual), pestaña
// principal de "Reclamos". Reemplaza a la lista armada alrededor de los
// cheques de la app anterior, que sigue disponible en la pestaña
// "Comisiones por cheque" (ClaimsDirectory).
//
// Cada tarjeta: fase y paso actual, días en el paso, plazo del documento del
// cliente (flowRules.js), aseguradora, PA y resumen de sus cheques.
// Vistas: Lista y Por fase (tablero de 4 columnas). Los reclamos terminados
// (Negociado, Retirado) siguen en la lista con su estado; se pueden ocultar.
//
// Filtros, orden, vista y página viven en la URL (useUrlParams): abrir una
// ficha y volver -- con el enlace "Volver a reclamos" o con Atrás -- deja la
// misma lista. La posición de scroll y la última ficha abierta se guardan en
// sessionStorage (ver RETURN_KEY). La lista se pagina de a PAGE_SIZE y el
// tablero muestra PAGE_SIZE por fase con "Mostrar más".
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useUrlParams from '../../hooks/useUrlParams';
import { matchesStepFilter } from '../../utils/claimHelpers';
import { rememberListPosition, peekListPosition, clearListPosition, scrollToListPosition } from '../../utils/listReturn';
import { CLAIM_FLOW, CLAIM_PHASES, FINAL_STEPS } from '../../config/claimFlow';
import { findStatus } from '../../config/claimStatuses';
import { getStepTimer } from '../../config/flowRules';
import { claimStepLabel } from '../../services/flowService';
import { money } from '../../documents/closingData';
import { TimerBadge } from './FlowBits';
import StatusBadge from './StatusBadge';
import useStatusCatalog from '../../hooks/useStatusCatalog';
import PaCards from './PaCards';
import './ClaimRecordList.css';

const millis = t => (t?.toMillis ? t.toMillis() : t ? new Date(t).getTime() || 0 : 0);
const daysIn = cr => {
  const ms = millis(cr.flow?.enteredAt);
  return ms ? Math.max(0, Math.floor((Date.now() - ms) / 86400000)) : null;
};
// Nombres migrados escritos TODO en mayúsculas: se ven más pesados que el resto; solo se aligera su peso, el dato no se toca.
const isAllCaps = name => !!name && name.length > 3 && name === name.toUpperCase() && name !== name.toLowerCase();
const isClosed = cr => FINAL_STEPS.includes(cr.flow?.stepId);
const urgent = timer => timer && (timer.status === 'overdue' || timer.status === 'warning');

export const PAGE_SIZE = 25;

const SORTS = {
  recent: (a, b) => millis(b.updatedAt || b.createdAt) - millis(a.updatedAt || a.createdAt),
  waiting: (a, b) => (daysIn(b) ?? -1) - (daysIn(a) ?? -1),
  client: (a, b) => (a.clientName || '').localeCompare(b.clientName || ''),
  claim: (a, b) => (a.claimNumber || '~').localeCompare(b.claimNumber || '~')
};

// companyPicker=false: la compañía ya se elige arriba (ClaimsDirectory) y la lista llega filtrada.
// adjustersAlwaysOpen: el panel "Por ajustador" queda siempre abierto (P.A Admin).
const ClaimRecordList = ({ claimRecords, checks = [], adjusters = [], language, isAdmin, lockedCompany = null, lockedAdjuster = null, companyPicker = true, adjustersAlwaysOpen = false }) => {
  const es = language === 'es';
  const location = useLocation();
  const [params, setParams] = useUrlParams({ q: '', step: 'all', co: 'all', pa: 'all', urgent: '', hide: '', sort: 'recent', view: 'list', page: '1', os: 'all' });
  const search = params.q;
  const stepFilter = params.step;
  const companyFilter = params.co;
  const paFilter = params.pa;
  const statusFilter = params.os;
  const statusCatalog = useStatusCatalog();
  const onlyUrgent = params.urgent === '1';
  const hideClosed = params.hide === '1';
  const sort = SORTS[params.sort] ? params.sort : 'recent';
  const view = params.view === 'board' ? 'board' : 'list';
  // Cualquier cambio de filtro/orden vuelve a la página 1.
  const setFilters = patch => setParams({ ...patch, page: undefined });
  const [columnLimits, setColumnLimits] = useState({});
  const controlsRef = useRef(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [adjustersOpen, setAdjustersOpen] = useState(false);

  // Cheques de cada reclamo (resumen en la tarjeta).
  const checksByRecord = useMemo(() => {
    const map = new Map();
    checks.forEach(check => {
      if (!check.claimRecordId) return;
      const entry = map.get(check.claimRecordId) || { count: 0, total: 0, inProgress: 0 };
      entry.count += 1;
      entry.total += Number(check.amount) || 0;
      if (check.flow?.phase === 'A') entry.inProgress += 1;
      map.set(check.claimRecordId, entry);
    });
    return map;
  }, [checks]);

  const rows = useMemo(() => claimRecords.map(cr => ({ cr, timer: getStepTimer('claim', cr), checks: checksByRecord.get(cr.id) })), [claimRecords, checksByRecord]);
  const companies = useMemo(() => Array.from(new Set(claimRecords.map(cr => cr.companyName).filter(Boolean))).sort(), [claimRecords]);
  // PA principal (el de la comisión) con cuántos reclamos tiene, para el filtro.
  const paCounts = useMemo(() => {
    const counts = new Map();
    claimRecords
      .filter(cr => companyFilter === 'all' || cr.companyName === companyFilter)
      .forEach(cr => { if (cr.adjusterName) counts.set(cr.adjusterName, (counts.get(cr.adjusterName) || 0) + 1); });
    return counts;
  }, [claimRecords, companyFilter]);
  const pas = useMemo(() => [...paCounts.keys()].sort(), [paCounts]);
  // Números de cada P.A para su tarjeta: reclamos activos (sin cerrar) y cheques.
  const paStats = useMemo(() => {
    const stats = new Map();
    claimRecords
      .filter(cr => cr.adjusterName && (companyFilter === 'all' || cr.companyName === companyFilter))
      .forEach(cr => {
        const entry = stats.get(cr.adjusterName) || { active: 0, checks: 0 };
        if (!isClosed(cr)) entry.active += 1;
        entry.checks += checksByRecord.get(cr.id)?.count || 0;
        stats.set(cr.adjusterName, entry);
      });
    return stats;
  }, [claimRecords, companyFilter, checksByRecord]);
  const showAdjusters = !lockedAdjuster && (adjustersAlwaysOpen || adjustersOpen);

  const matchesStep = cr => matchesStepFilter(cr, stepFilter);

  const term = search.trim().toLowerCase();
  const matchesSearch = cr => !term || [
    cr.clientName, cr.claimNumber, cr.policyNumber, cr.insuranceCompanyName, cr.clientAddress, cr.lossAddressStreet,
    cr.owner2?.firstName, cr.owner2?.lastName, cr.adjusterName, cr.oaStatus
  ].some(value => String(value || '').toLowerCase().includes(term));

  // Precedencia estado vs. "ocultar terminados": si el filtro de paso apunta
  // a un paso terminado (Negociado/Retirado), gana el paso -- se piden esos
  // reclamos a propósito -- y la casilla queda deshabilitada con su motivo.
  // En cualquier otro caso "ocultar" quita los terminados de todo resultado.
  const stepIsClosed = FINAL_STEPS.includes(stepFilter);
  const hideApplies = hideClosed && !stepIsClosed;
  const closedCount = rows.filter(r => isClosed(r.cr)).length;
  const filtered = useMemo(() => rows
    .filter(({ cr, timer }) => (!hideApplies || !isClosed(cr))
      && matchesStep(cr)
      && matchesSearch(cr)
      && (lockedCompany || !companyPicker || companyFilter === 'all' || cr.companyName === companyFilter)
      && (lockedAdjuster || paFilter === 'all' || cr.adjusterName === paFilter)
      && (statusFilter === 'all' || (statusFilter === 'none' ? !cr.oaStatus : String(cr.oaStatus || '').toLowerCase() === statusFilter.toLowerCase()))
      && (!onlyUrgent || urgent(timer)))
    .sort((a, b) => SORTS[sort](a.cr, b.cr)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rows, hideApplies, stepFilter, term, companyFilter, paFilter, statusFilter, onlyUrgent, sort, lockedCompany, lockedAdjuster, companyPicker]);

  // Paginación de la lista: el filtro y el orden ya se aplicaron a TODO el
  // conjunto; solo se recorta lo que se dibuja.
  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const page = Math.min(pageCount, Math.max(1, parseInt(params.page, 10) || 1));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);
  const pageRows = view === 'list' ? filtered.slice(from - 1, to) : filtered;
  const goToPage = next => {
    setParams({ page: next });
    controlsRef.current?.scrollIntoView?.({ block: 'start' });
  };

  const activeFilters = [
    term && { label: `"${search.trim()}"`, clear: () => setFilters({ q: '' }) },
    stepFilter !== 'all' && { label: stepFilter.startsWith('phase:') ? `${es ? 'Fase' : 'Phase'} ${stepFilter.slice(6)}` : stepFilter === 'none' ? (es ? 'Sin paso' : 'No step') : (claimStepLabel({ flow: { stepId: stepFilter } }, language) || stepFilter), clear: () => setFilters({ step: 'all' }) },
    !lockedCompany && companyPicker && companyFilter !== 'all' && { label: companyFilter, clear: () => setFilters({ co: 'all', pa: 'all' }) },
    !lockedAdjuster && paFilter !== 'all' && { label: paFilter, clear: () => setFilters({ pa: 'all' }) },
    statusFilter !== 'all' && { label: statusFilter === 'none' ? (es ? 'Sin estado' : 'No status') : (findStatus(statusCatalog, statusFilter)?.name || statusFilter), clear: () => setFilters({ os: 'all' }) },
    onlyUrgent && { label: es ? 'Con plazo por vencer' : 'Deadline due', clear: () => setFilters({ urgent: '' }) },
    hideApplies && { label: es ? 'Terminados ocultos' : 'Finished hidden', clear: () => setFilters({ hide: '' }) }
  ].filter(Boolean);
  // "Limpiar" también apaga una casilla marcada cuyo efecto no se aplica.
  const canClear = activeFilters.length > 0 || hideClosed;
  const clearAll = () => setFilters({ q: '', step: 'all', ...(companyPicker ? { co: 'all' } : {}), pa: 'all', os: 'all', urgent: '', hide: '' });

  const phaseLabel = phase => {
    const p = CLAIM_PHASES.find(x => x.id === phase);
    if (p) return `${es ? 'Fase' : 'Phase'} ${p.id} · ${es ? p.label.es : p.label.en}`;
    if (phase === 'end') return es ? 'Terminado' : 'Finished';
    return es ? 'Sin paso' : 'No step';
  };

  // Al abrir una ficha se recuerda dónde estaba la lista (scroll y ficha)
  // para dejarla igual al volver.
  const rememberPosition = id => rememberListPosition(location.search, id, { tab: 'claims' });
  useEffect(() => {
    const saved = peekListPosition(location.search);
    if (!saved || saved.tab !== 'claims') return undefined;
    clearListPosition();
    const frame = requestAnimationFrame(() => scrollToListPosition(saved));
    return () => cancelAnimationFrame(frame);
    // Solo al montar: la lista vuelve con la URL que dejó.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const checksSummary = summary => {
    if (!summary) return <span className="crl-empty-value">{es ? 'Sin cheques' : 'No checks'}</span>;
    return (
      <>
        <span>{es ? plural(summary.count, 'cheque', 'cheques') : plural(summary.count, 'check', 'checks')}</span>
        <span className="crl-checks-total">{es ? `${money(summary.total) || '$0'} en cheques` : `${money(summary.total) || '$0'} in checks`}</span>
        {summary.inProgress > 0 && <span className="crl-checks-total">{es ? `${summary.inProgress} en curso` : `${summary.inProgress} in progress`}</span>}
      </>
    );
  };

  const card = ({ cr, timer, checks: summary }, compact = false) => {
    const days = daysIn(cr);
    const phase = cr.flow?.phase;
    return (
      <Link key={cr.id} to={`/claim-status/claim/${cr.id}`} state={{ from: `${location.pathname}${location.search}` }} data-claim-id={cr.id} onClick={() => rememberPosition(cr.id)}
        className={`crl-card ${urgent(timer) ? 'is-urgent' : ''} ${isClosed(cr) ? 'is-closed' : ''}`}>
        <h3 className={`crl-client ${isAllCaps(cr.clientName) ? 'is-caps' : ''}`}>{cr.clientName || 'N/A'}</h3>
        <div className="crl-card-top">
          <span className="crl-number">{cr.claimNumber ? `#${cr.claimNumber}` : (es ? 'Sin número' : 'No number')}</span>
          {!compact && <span className={`crl-phase crl-phase-${phase ?? 'none'}`}>{phaseLabel(phase)}</span>}
        </div>
        <div className="crl-step">
          <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
          <span>{claimStepLabel(cr, language) || (es ? 'Sin paso asignado' : 'No step assigned')}</span>
          {days !== null && <span className="crl-days">· {days} {es ? (days === 1 ? 'día' : 'días') : (days === 1 ? 'day' : 'days')}</span>}
          {/* Sin repetir: si el estado dice lo mismo que el paso (p. ej. "Settled"), basta con uno. */}
          {cr.oaStatus && cr.oaStatus.trim().toLowerCase() !== String(claimStepLabel(cr, language) || '').trim().toLowerCase() && <span className="crl-status"><StatusBadge name={cr.oaStatus} catalog={statusCatalog} /></span>}
        </div>
        {timer && (urgent(timer) || timer.kind === 'unlock') && <TimerBadge timer={timer} es={es} />}
        {!compact && (
          <>
            <div className="crl-insurer" title={cr.insuranceCompanyName || undefined}>
              <span className="crl-label">{es ? 'Aseguradora' : 'Insurance company'}</span>
              {cr.insuranceCompanyName || <span className="crl-empty-value">{es ? 'Sin aseguradora' : 'No insurance company'}</span>}
            </div>
            <div className="crl-owner">
              <span className="crl-label">{es ? 'Responsable' : 'Owner'}</span>
              {!lockedAdjuster && (cr.adjusterName || <span className="crl-empty-value">{es ? 'Sin PA' : 'No PA'}</span>)}
              {!lockedCompany && cr.companyName && <span className="crl-owner-company">{cr.companyName}</span>}
            </div>
          </>
        )}
        <div className="crl-checks">
          <span className="crl-label">{es ? 'Cheques (monto bruto)' : 'Checks (gross amount)'}</span>
          {checksSummary(summary)}
        </div>
      </Link>
    );
  };

  const pager = view === 'list' && total > PAGE_SIZE && (
    <nav className="crl-pager" aria-label={es ? 'Paginación de reclamos' : 'Claims pagination'}>
      <span className="crl-range">{es ? `Mostrando ${from}–${to} de ${total}` : `Showing ${from}–${to} of ${total}`}</span>
      <div className="crl-pager-buttons">
        <button type="button" className="cd-btn" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
          <i className="fa-solid fa-chevron-left" aria-hidden="true" /> {es ? 'Anterior' : 'Previous'}
        </button>
        <span className="crl-page-indicator">{es ? `Página ${page} de ${pageCount}` : `Page ${page} of ${pageCount}`}</span>
        <button type="button" className="cd-btn" disabled={page >= pageCount} onClick={() => goToPage(page + 1)}>
          {es ? 'Siguiente' : 'Next'} <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );

  return (
    <section className="crl" aria-label={es ? 'Reclamos' : 'Claims'}>
      <div className="crl-controls" ref={controlsRef}>
        <div className="crl-bar">
        <div className="cd-filter-search">
          <i className="fa-solid fa-search" aria-hidden="true"></i>
          <input type="search" value={search} onChange={e => setFilters({ q: e.target.value })}
            aria-label={es ? 'Buscar reclamo' : 'Search claim'}
            title={es ? 'Busca por cliente, Claim #, póliza, aseguradora, dirección, ajustador o estado' : 'Search by client, claim #, policy, insurance, address, adjuster or status'}
            placeholder={es ? 'Buscar cliente, Claim # o póliza…' : 'Search client, claim # or policy…'} />
        </div>
          {!lockedAdjuster && !adjustersAlwaysOpen && pas.length > 0 && (
            <button type="button" className={`crl-filter-toggle ${adjustersOpen || paFilter !== 'all' ? 'is-open' : ''}`} aria-expanded={adjustersOpen} aria-controls="crl-adjuster-panel" onClick={() => setAdjustersOpen(open => !open)}>
              <i className="fa-solid fa-user-tie" aria-hidden="true" /> {es ? 'Por ajustador' : 'By adjuster'}
            </button>
          )}
          <button type="button" className={`crl-filter-toggle ${filtersOpen ? 'is-open' : ''}`} aria-expanded={filtersOpen} aria-controls="crl-filter-panel" onClick={() => setFiltersOpen(open => !open)}>
            <i className="fa-solid fa-sliders" aria-hidden="true" /> {es ? 'Filtros y orden' : 'Filters & sort'}{activeFilters.length > 0 && <span className="crl-filter-count">{activeFilters.length}</span>}
          </button>
        <div className="crl-toolbar">
          <div className="cd-view-toggle" role="group" aria-label={es ? 'Vista' : 'View'}>
            <button type="button" className={`cd-view-btn ${view === 'list' ? 'active' : ''}`} aria-pressed={view === 'list'} onClick={() => setParams({ view: 'list' })}>
              <i className="fa-solid fa-list" aria-hidden="true"></i> {es ? 'Lista' : 'List'}
            </button>
            <button type="button" className={`cd-view-btn ${view === 'board' ? 'active' : ''}`} aria-pressed={view === 'board'} onClick={() => setParams({ view: 'board' })}>
              <i className="fa-solid fa-table-columns" aria-hidden="true"></i> {es ? 'Por fase' : 'By phase'}
            </button>
          </div>
        </div>
        </div>
        {showAdjusters && pas.length > 0 && (
          <PaCards
            id="crl-adjuster-panel"
            title={adjustersAlwaysOpen ? (es ? 'Tus P.A' : 'Your P.As') : null}
            clearLabel={es ? 'Ver todos' : 'Show all'}
            ariaLabel={es ? 'Filtrar por ajustador' : 'Filter by adjuster'}
            selected={paFilter}
            onSelect={name => setFilters({ pa: name })}
            items={[...pas].sort((a, b) => paCounts.get(b) - paCounts.get(a) || a.localeCompare(b)).map(name => {
              const stats = paStats.get(name) || { active: 0, checks: 0 };
              return {
                name, profile: adjusters.find(item => String(item.name || '').trim().toLowerCase() === String(name).trim().toLowerCase()), active: stats.active > 0,
                stats: [
                  { label: es ? 'Reclamos' : 'Claims', value: paCounts.get(name) },
                  { label: es ? 'Activos' : 'Active', value: stats.active },
                  { label: es ? 'Cheques' : 'Checks', value: stats.checks }
                ]
              };
            })}
          />
        )}
        {filtersOpen && (
        <div className="crl-filter-panel" id="crl-filter-panel">
        <div className="crl-filters">
          <div className="cd-select-wrap">
            <i className="fa-solid fa-route" aria-hidden="true"></i>
            <select aria-label={es ? 'Paso del reclamo' : 'Claim step'} value={stepFilter} onChange={e => setFilters({ step: e.target.value })}>
              <option value="all">{es ? 'Todos los pasos' : 'All steps'}</option>
              {CLAIM_PHASES.map(phase => (
                <optgroup key={phase.id} label={`${phase.id}. ${es ? phase.label.es : phase.label.en}`}>
                  <option value={`phase:${phase.id}`}>{es ? `Toda la fase ${phase.id}` : `All of phase ${phase.id}`}</option>
                  {CLAIM_FLOW.steps.filter(st => st.phase === phase.id).map(st => <option key={st.id} value={st.id}>{es ? st.label.es : st.label.en}</option>)}
                </optgroup>
              ))}
              <optgroup label={es ? 'Cierre' : 'Closed'}>
                {CLAIM_FLOW.steps.filter(st => st.phase === 'end').map(st => <option key={st.id} value={st.id}>{es ? st.label.es : st.label.en}</option>)}
              </optgroup>
              <option value="none">{es ? 'Sin paso asignado' : 'No step'}</option>
            </select>
            <i className="fa-solid fa-chevron-down cd-select-caret" aria-hidden="true"></i>
          </div>
          {!lockedCompany && companyPicker && isAdmin && (
            <div className="cd-select-wrap">
              <i className="fa-solid fa-building" aria-hidden="true"></i>
              <select aria-label={es ? 'Empresa' : 'Company'} value={companyFilter} onChange={e => setFilters({ co: e.target.value, pa: 'all' })}>
                <option value="all">{es ? 'Todas las empresas' : 'All companies'}</option>
                {companies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <i className="fa-solid fa-chevron-down cd-select-caret" aria-hidden="true"></i>
            </div>
          )}
          {!lockedAdjuster && (
            <div className="cd-select-wrap">
              <i className="fa-solid fa-user-tie" aria-hidden="true"></i>
              <select aria-label="PA" value={paFilter} onChange={e => setFilters({ pa: e.target.value })}>
                <option value="all">{es ? 'Todos los PA' : 'All PAs'}</option>
                {pas.map(name => <option key={name} value={name}>{`${name} (${paCounts.get(name)})`}</option>)}
              </select>
              <i className="fa-solid fa-chevron-down cd-select-caret" aria-hidden="true"></i>
            </div>
          )}
          <div className="cd-select-wrap">
            <i className="fa-solid fa-tag" aria-hidden="true"></i>
            <select aria-label={es ? 'Estado (Organized)' : 'Status (Organized)'} value={statusFilter} onChange={e => setFilters({ os: e.target.value })}>
              <option value="all">{es ? 'Todos los estados' : 'All statuses'}</option>
              {statusCatalog.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
              <option value="none">{es ? 'Sin estado' : 'No status'}</option>
            </select>
            <i className="fa-solid fa-chevron-down cd-select-caret" aria-hidden="true"></i>
          </div>
          <div className="cd-select-wrap">
            <i className="fa-solid fa-arrow-down-wide-short" aria-hidden="true"></i>
            <select aria-label={es ? 'Ordenar reclamos por' : 'Sort claims by'} value={sort} onChange={e => setFilters({ sort: e.target.value })}>
              <option value="recent">{es ? 'Actividad reciente' : 'Recent activity'}</option>
              <option value="waiting">{es ? 'Más días en el paso' : 'Longest in step'}</option>
              <option value="client">{es ? 'Cliente (A-Z)' : 'Client (A-Z)'}</option>
              <option value="claim">Claim #</option>
            </select>
            <i className="fa-solid fa-chevron-down cd-select-caret" aria-hidden="true"></i>
          </div>
        </div>
        <div className="crl-filter-options">
          <label className="crl-toggle">
            <input type="checkbox" checked={onlyUrgent} onChange={e => setFilters({ urgent: e.target.checked ? '1' : '' })} />
            {es ? 'Solo con plazo por vencer' : 'Only with a deadline due'}
          </label>
          <label className="crl-toggle">
            <input type="checkbox" checked={hideClosed} disabled={stepIsClosed} onChange={e => setFilters({ hide: e.target.checked ? '1' : '' })} />
            {es ? `Ocultar negociados y retirados (${closedCount})` : `Hide settled & withdrawn (${closedCount})`}
          </label>
          {stepIsClosed && (
            <p className="crl-filter-note">{es ? 'No aplica mientras filtras por un paso terminado: ese paso ya muestra esos reclamos.' : 'Not applied while filtering by a finished step: that step already shows those claims.'}</p>
          )}
        </div>
        </div>
        )}
        <div className="cd-filter-feedback">
          <div className="cd-active-filters" aria-label={es ? 'Filtros activos' : 'Active filters'}>
            <span className="crl-count" aria-live="polite">{total} {es ? 'reclamo(s)' : 'claim(s)'}</span>
            {view === 'list' && total > PAGE_SIZE && <span className="crl-range-inline">{es ? `mostrando ${from}–${to}` : `showing ${from}–${to}`}</span>}
            {activeFilters.map(filter => (
              <button type="button" className="cd-filter-chip" key={filter.label} onClick={filter.clear} aria-label={`${es ? 'Quitar filtro' : 'Remove filter'}: ${filter.label}`}>
                <span>{filter.label}</span><i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            ))}
          </div>
          <button type="button" className="cd-clear-filters" disabled={!canClear} onClick={clearAll}>
            <i className="fa-solid fa-rotate-left" aria-hidden="true" />{es ? 'Limpiar filtros' : 'Clear filters'}
          </button>
        </div>
      </div>

      {total === 0 && (
        <div className="cd-no-results">
          <i className="fa-solid fa-folder-open" aria-hidden="true"></i>
          <p>{claimRecords.length === 0 ? (es ? 'Todavía no hay reclamos.' : 'No claims yet.') : (es ? 'Ningún reclamo coincide con los filtros.' : 'No claims match the filters.')}</p>
          {claimRecords.length > 0 && canClear && (
            <button type="button" className="cd-btn" onClick={clearAll}>
              <i className="fa-solid fa-rotate-left" aria-hidden="true" /> {es ? 'Limpiar filtros' : 'Clear filters'}
            </button>
          )}
        </div>
      )}

      {view === 'list' && total > 0 && (
        <div className="crl-grid"><div className="crl-list-heading" aria-hidden="true"><span>{es ? 'Cliente / reclamo' : 'Client / claim'}</span><span>{es ? 'Paso actual' : 'Current step'}</span><span>{es ? 'Aseguradora' : 'Insurance company'}</span><span>{es ? 'PA / empresa' : 'PA / company'}</span><span>{es ? 'Cheques (bruto)' : 'Checks (gross)'}</span></div>{pageRows.map(row => card(row))}</div>
      )}
      {pager}

      {view === 'board' && total > 0 && (
        <div className="crl-board">
          {[...CLAIM_PHASES.map(p => p.id), ...(filtered.some(r => r.cr.flow?.phase === 'end') ? ['end'] : []), ...(filtered.some(r => !r.cr.flow?.phase) ? ['none'] : [])].map(phase => {
            const items = filtered.filter(r => (r.cr.flow?.phase ?? 'none') === phase);
            const limit = columnLimits[phase] || PAGE_SIZE;
            return (
              <div key={phase} className="crl-column">
                <div className={`crl-column-head crl-phase-${phase}`}>
                  <span>{phaseLabel(phase === 'none' ? undefined : phase)}</span>
                  <span className="crl-column-count">{items.length}</span>
                </div>
                <div className="crl-column-body">
                  {items.length === 0 ? <p className="crl-column-empty">{es ? 'Nada aquí' : 'Nothing here'}</p> : items.slice(0, limit).map(row => card(row, true))}
                  {items.length > limit && (
                    <button type="button" className="cd-btn crl-column-more" onClick={() => setColumnLimits(current => ({ ...current, [phase]: limit + PAGE_SIZE }))}>
                      {es ? `Mostrar más (${limit} de ${items.length})` : `Show more (${limit} of ${items.length})`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ClaimRecordList;
